import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCog, 
  faDownload, 
  faChartBar,
  faTruck,
  faCalendarAlt,
  faRoad,
  faTachometerAlt,
  faClock,
  faGasPump,
  faExclamationTriangle,
  faTimes,
  faExpand,
  faCompress,
  faColumns,
  faWindowRestore,
  faRedo,
  faTable,
  faList
} from '@fortawesome/free-solid-svg-icons';
import './CommonStatReport.css';

const CommonStatReport = ({ vehicle, startDate, endDate }) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTableView, setIsTableView] = useState(false);
  const [settings, setSettings] = useState({
    showVehicleGroup: true,
    showVehicleName: true,
    showDate: true,
    showMileage: true,
    showTotalMileage: true,
    showEngineHours: true,
    showSpeedViolations: true,
    showAverageSpeed: true,
    showMaxSpeed: true,
    showMovementTime: true,
    showEngineTime: true,
    showEngineMovementTime: true,
    showEngineIdleTime: true,
    showIdleTime: true,
    showNormalRpmTime: true,
    showMaxRpmTime: true,
    showEngineOffTime: true,
    showEngineLoadTime: true,
    showFuelStart: true,
    showFuelEnd: true,
    showFuelConsumption: true,
    showFuelRefills: true,
    showFuelDrains: true,
    showFuelMin: true,
    showFuelMax: true,
    showFuelPer100km: true,
    showFuelPer100kmMovement: true,
    showFuelMovement: true,
    showFuelIdle: true,
    showFuelNorm: true,
    showFuelNormCalculated: true,
    showFuelDeviation: true,
    showFuelOverconsumption: true,
    showFuelPerHour: true,
    showFuelPerEngineHour: true,
    showFuelPerHourIdle: true,
    showFuelPerHourMovement: true,
    showFuelNormPerHour: true,
    showFuelNormPerHourCalculated: true,
    showFuelDeviationPerHour: true,
    showFuelOverconsumptionPerHour: true,
    showFuelTotal: true,
    showFuelTotalIdle: true,
    showFuelIdleExcess: true,
    showEngineHoursValue: true,
    showOdometerStart: true,
    showOdometerEnd: true,
    showFuelCounter: true,
    showMaintenanceMileage: true,
    showMaintenanceHours: true,
    showSummaryMileage: true,
    showSummaryHours: true,
    showSummaryFuel: true
  });

  const reportRef = useRef(null);

  // Обработчик правого клика
  const handleContextMenu = (e) => {
    e.preventDefault();
    setShowSettingsModal(true);
  };

  // Функции для работы с глобальной системой разделения экрана
  const handleHorizontalSplit = () => {
    const container = reportRef.current?.closest('.split-screen-container');
    if (!container) {
      console.error('CommonStatReport: Не удалось найти контейнер для разделения');
      return;
    }
    
    // Активируем контейнер
    document.querySelectorAll('.split-screen-container[data-active="true"]')
      .forEach(el => {
        el.setAttribute('data-active', 'false');
        el.classList.remove('active-container');
      });
    
    container.setAttribute('data-active', 'true');
    container.classList.add('active-container');
    
    // Получаем ID контейнера
    const targetContainerId = container.id || container.getAttribute('data-container-id');
    if (!targetContainerId) {
      console.error('CommonStatReport: У контейнера нет ID для разделения');
      return;
    }
    
    console.log(`CommonStatReport: Разделяем контейнер ${targetContainerId} горизонтально`);
    
    // Создаем пользовательское событие для запроса на разделение
    const splitEvent = new CustomEvent('requestSplit', {
      detail: {
        direction: 'horizontal',
        targetContainerId: targetContainerId,
        timestamp: Date.now(),
        processed: false
      }
    });
    
    document.dispatchEvent(splitEvent);
  };

  const handleVerticalSplit = () => {
    const container = reportRef.current?.closest('.split-screen-container');
    if (!container) {
      console.error('CommonStatReport: Не удалось найти контейнер для разделения');
      return;
    }
    
    // Активируем контейнер
    document.querySelectorAll('.split-screen-container[data-active="true"]')
      .forEach(el => {
        el.setAttribute('data-active', 'false');
        el.classList.remove('active-container');
      });
    
    container.setAttribute('data-active', 'true');
    container.classList.add('active-container');
    
    // Получаем ID контейнера
    const targetContainerId = container.id || container.getAttribute('data-container-id');
    if (!targetContainerId) {
      console.error('CommonStatReport: У контейнера нет ID для разделения');
      return;
    }
    
    console.log(`CommonStatReport: Разделяем контейнер ${targetContainerId} вертикально`);
    
    // Создаем пользовательское событие для запроса на разделение
    const splitEvent = new CustomEvent('requestSplit', {
      detail: {
        direction: 'vertical',
        targetContainerId: targetContainerId,
        timestamp: Date.now(),
        processed: false
      }
    });
    
    document.dispatchEvent(splitEvent);
  };

  const handleSingleMode = () => {
    if (window.splitScreenManager && typeof window.splitScreenManager.changeSplitMode === 'function') {
      window.splitScreenManager.changeSplitMode('SINGLE');
    } else if (window.splitScreenManager && typeof window.splitScreenManager.clearSplit === 'function') {
      window.splitScreenManager.clearSplit();
    }
  };

  const handleGoBack = () => {
    if (window.splitScreenManager && typeof window.splitScreenManager.goBack === 'function') {
      window.splitScreenManager.goBack();
    }
  };

  // Загрузка данных отчета
  const loadReportData = async () => {
    if (!vehicle || !startDate || !endDate) {
      return;
    }

    setIsLoading(true);
    try {
      // Имитация загрузки данных
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Генерируем тестовые данные
      const mockData = {
        vehicleGroup: 'Грузовой транспорт',
        vehicleName: vehicle.name,
        date: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
        mileage: Math.floor(Math.random() * 1000) + 100,
        totalMileageStart: Math.floor(Math.random() * 50000) + 10000,
        totalMileageEnd: Math.floor(Math.random() * 50000) + 10000,
        engineHours: Math.floor(Math.random() * 100) + 10,
        speedViolations: Math.floor(Math.random() * 50) + 5,
        averageSpeed: Math.floor(Math.random() * 60) + 20,
        maxSpeed: Math.floor(Math.random() * 40) + 80,
        movementTime: '08:30:15',
        movementTimePercent: 65,
        engineTime: '12:45:30',
        engineTimePercent: 85,
        engineMovementTime: '08:30:15',
        engineMovementTimePercent: 65,
        engineIdleTime: '04:15:15',
        engineIdleTimePercent: 20,
        idleTime: '03:45:00',
        normalRpmTime: '10:30:00',
        normalRpmPercent: 75,
        maxRpmTime: '02:15:30',
        maxRpmPercent: 15,
        engineOffTime: '11:14:30',
        engineOffTimePercent: 15,
        engineLoadTime: '09:20:00',
        engineLoadTimePercent: 70,
        fuelStart: Math.floor(Math.random() * 100) + 50,
        fuelEnd: Math.floor(Math.random() * 100) + 50,
        fuelConsumption: Math.floor(Math.random() * 200) + 100,
        fuelRefills: Math.floor(Math.random() * 150) + 50,
        fuelDrains: Math.floor(Math.random() * 20) + 5,
        fuelMin: Math.floor(Math.random() * 30) + 10,
        fuelMax: Math.floor(Math.random() * 200) + 150,
        fuelPer100km: (Math.random() * 20 + 15).toFixed(1),
        fuelPer100kmMovement: (Math.random() * 18 + 12).toFixed(1),
        fuelMovement: Math.floor(Math.random() * 150) + 80,
        fuelIdle: Math.floor(Math.random() * 50) + 20,
        fuelNorm: (Math.random() * 5 + 12).toFixed(1),
        fuelNormCalculated: (Math.random() * 5 + 12).toFixed(1),
        fuelDeviation: (Math.random() * 20 - 10).toFixed(1),
        fuelOverconsumption: Math.floor(Math.random() * 50) + 10,
        fuelPerHour: (Math.random() * 8 + 6).toFixed(1),
        fuelPerEngineHour: (Math.random() * 8 + 6).toFixed(1),
        fuelPerHourIdle: (Math.random() * 3 + 2).toFixed(1),
        fuelPerHourMovement: (Math.random() * 10 + 8).toFixed(1),
        fuelNormPerHour: (Math.random() * 2 + 5).toFixed(1),
        fuelNormPerHourCalculated: (Math.random() * 2 + 5).toFixed(1),
        fuelDeviationPerHour: (Math.random() * 15 - 7.5).toFixed(1),
        fuelOverconsumptionPerHour: Math.floor(Math.random() * 30) + 5,
        fuelTotal: Math.floor(Math.random() * 200) + 100,
        fuelTotalIdle: Math.floor(Math.random() * 50) + 20,
        fuelIdleExcess: Math.floor(Math.random() * 20) + 5,
        engineHoursValue: '12:45:30',
        odometerStart: Math.floor(Math.random() * 50000) + 10000,
        odometerEnd: Math.floor(Math.random() * 50000) + 10000,
        fuelCounter: Math.floor(Math.random() * 1000) + 500,
        maintenanceMileage: Math.floor(Math.random() * 5000) + 1000,
        maintenanceHours: Math.floor(Math.random() * 200) + 50,
        summaryMileage: Math.floor(Math.random() * 1000) + 100,
        summaryHours: '12:45:30',
        summaryFuel: Math.floor(Math.random() * 200) + 100
      };

      setReportData(mockData);
    } catch (error) {
      console.error('Ошибка загрузки данных отчета:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReportData();
  }, [vehicle, startDate, endDate]);

  // Обработчик событий разделения экрана
  useEffect(() => {
    const handleSplitContainerComplete = (event) => {
      // Обработка завершения разделения контейнера
      console.log('CommonStatReport: Разделение контейнера завершено', event.detail);
    };

    // Подписываемся на события разделения экрана
    document.addEventListener('splitContainerComplete', handleSplitContainerComplete);

    return () => {
      document.removeEventListener('splitContainerComplete', handleSplitContainerComplete);
    };
  }, []);

  // Обработчик изменения настроек
  const handleSettingChange = (settingName) => {
    setSettings(prev => ({
      ...prev,
      [settingName]: !prev[settingName]
    }));
  };

  // Экспорт отчета
  const exportReport = () => {
    // Логика экспорта отчета
    console.log('Экспорт отчета');
  };

  // Функции управления разделением экрана
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleTableView = () => {
    setIsTableView(!isTableView);
  };

  // Рендер строки отчета
  const renderReportRow = (label, value, icon, enabled = true) => {
    if (!enabled) return null;
    
    return (
      <div className="report-row">
        <div className="report-label">
          <FontAwesomeIcon icon={icon} className="report-icon" />
          {label}
        </div>
        <div className="report-value">{value}</div>
      </div>
    );
  };

  // Рендер табличного вида
  const renderTableView = () => {
    if (!reportData) return null;

    return (
      <div className="table-view-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Параметр</th>
              <th>Значение</th>
              <th>Дата</th>
              <th>Пробег, км</th>
              <th>Общий пробег на начало периода, км</th>
              <th>Общий пробег на конец периода, км</th>
              <th>Значение счетчика моточасов по оборотам</th>
              <th>Пробег с превышением скорости, км</th>
              <th>Средняя скорость в движении, км/ч</th>
              <th>Максимальная скорость, км/ч</th>
              <th>Время движения</th>
              <th>Время работы двигателя</th>
              <th>Время работы двигателя в движении</th>
              <th>Время работы двигателя без движения</th>
              <th>Время работы двигателя на холостом ходу</th>
              <th>Время работы двигателя на нормальных оборотах</th>
              <th>Время работы двигателя на предельных оборотах</th>
              <th>Время с выключенным двигателем</th>
              <th>Время работы двигателя под нагрузкой</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="table-section">Основная информация</td>
              <td>{reportData.vehicleGroup}</td>
              <td>{reportData.date}</td>
              <td>{`${reportData.mileage} км`}</td>
              <td>{`${reportData.totalMileageStart} км`}</td>
              <td>{`${reportData.totalMileageEnd} км`}</td>
              <td>{`${reportData.engineHours} ч`}</td>
              <td>{`${reportData.speedViolations} км`}</td>
              <td>{`${reportData.averageSpeed} км/ч`}</td>
              <td>{`${reportData.maxSpeed} км/ч`}</td>
              <td>{`${reportData.movementTime} (${reportData.movementTimePercent}%)`}</td>
              <td>{`${reportData.engineTime} (${reportData.engineTimePercent}%)`}</td>
              <td>{`${reportData.engineMovementTime} (${reportData.engineMovementTimePercent}%)`}</td>
              <td>{`${reportData.engineIdleTime} (${reportData.engineIdleTimePercent}%)`}</td>
              <td>{reportData.idleTime}</td>
              <td>{`${reportData.normalRpmTime} (${reportData.normalRpmPercent}%)`}</td>
              <td>{`${reportData.maxRpmTime} (${reportData.maxRpmPercent}%)`}</td>
              <td>{`${reportData.engineOffTime} (${reportData.engineOffTimePercent}%)`}</td>
              <td>{`${reportData.engineLoadTime} (${reportData.engineLoadTimePercent}%)`}</td>
            </tr>
            
            
            <tr className="summary-row">
              <td className="table-section">Итоги: </td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div 
      className={`common-stat-report ${isFullscreen ? 'fullscreen' : ''}`} 
      ref={reportRef} 
      onContextMenu={handleContextMenu}
    >
      <div className="report-header">
        <div className="report-title">
          <FontAwesomeIcon icon={faChartBar} />
          <h2>Сводный отчет</h2>
        </div>
        <div className="report-actions">
          <button className="action-btn" onClick={() => setShowSettingsModal(true)} title="Настройки">
            <FontAwesomeIcon icon={faCog} />
          </button>
          <button className="action-btn" onClick={handleHorizontalSplit} title="Разделить по горизонтали">
            <FontAwesomeIcon icon={faColumns} style={{ transform: 'rotate(90deg)' }} />
          </button>
          <button className="action-btn" onClick={handleVerticalSplit} title="Разделить по вертикали">
            <FontAwesomeIcon icon={faColumns} />
          </button>
          <button className="action-btn" onClick={handleSingleMode} title="Один экран">
            <FontAwesomeIcon icon={faWindowRestore} />
          </button>
          <button className="action-btn" onClick={handleGoBack} title="Вернуться к предыдущему режиму">
            <FontAwesomeIcon icon={faRedo} style={{ transform: 'rotate(180deg)' }} />
          </button>
          <button className="action-btn" onClick={toggleFullscreen} title={isFullscreen ? "Свернуть" : "Развернуть"}>
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </button>
          <button className="action-btn" onClick={toggleTableView} title={isTableView ? "Перейти к табличному виду" : "Перейти к графическому виду"}>
            <FontAwesomeIcon icon={isTableView ? faTable : faList} />
          </button>
          <button className="action-btn" onClick={exportReport} title="Экспорт">
            <FontAwesomeIcon icon={faDownload} />
          </button>
        </div>
      </div>

      <div className="report-content">
        {isLoading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Загрузка данных отчета...</p>
          </div>
        ) : reportData ? (
          isTableView ? (
            renderTableView()
          ) : (
            <div className="report-sections">
              {/* Основная информация */}
              <div className="report-section">
                <h3>Основная информация</h3>
                {renderReportRow('Группа транспортных средств', reportData.vehicleGroup, faTruck, settings.showVehicleGroup)}
                {renderReportRow('Название ТС', reportData.vehicleName, faTruck, settings.showVehicleName)}
                {renderReportRow('Дата', reportData.date, faCalendarAlt, settings.showDate)}
                {renderReportRow('Пробег, км', `${reportData.mileage} км`, faRoad, settings.showMileage)}
                {renderReportRow('Общий пробег на начало периода, км', `${reportData.totalMileageStart} км`, faRoad, settings.showTotalMileage)}
                {renderReportRow('Общий пробег на конец периода, км', `${reportData.totalMileageEnd} км`, faRoad, settings.showTotalMileage)}
                {renderReportRow('Значение счетчика моточасов по оборотам', `${reportData.engineHours} ч`, faClock, settings.showEngineHours)}
              </div>

              {/* Скорость */}
              <div className="report-section">
                <h3>Скорость</h3>
                {renderReportRow('Пробег с превышением скорости, км', `${reportData.speedViolations} км`, faExclamationTriangle, settings.showSpeedViolations)}
                {renderReportRow('Средняя скорость в движении, км/ч', `${reportData.averageSpeed} км/ч`, faTachometerAlt, settings.showAverageSpeed)}
                {renderReportRow('Максимальная скорость, км/ч', `${reportData.maxSpeed} км/ч`, faTachometerAlt, settings.showMaxSpeed)}
              </div>

              {/* Время */}
              <div className="report-section">
                <h3>Время работы</h3>
                {renderReportRow('Время движения', `${reportData.movementTime} (${reportData.movementTimePercent}%)`, faClock, settings.showMovementTime)}
                {renderReportRow('Время работы двигателя', `${reportData.engineTime} (${reportData.engineTimePercent}%)`, faClock, settings.showEngineTime)}
                {renderReportRow('Время работы двигателя в движении', `${reportData.engineMovementTime} (${reportData.engineMovementTimePercent}%)`, faClock, settings.showEngineMovementTime)}
                {renderReportRow('Время работы двигателя без движения', `${reportData.engineIdleTime} (${reportData.engineIdleTimePercent}%)`, faClock, settings.showEngineIdleTime)}
                {renderReportRow('Время работы двигателя на холостом ходу', reportData.idleTime, faClock, settings.showIdleTime)}
                {renderReportRow('Время работы двигателя на нормальных оборотах', `${reportData.normalRpmTime} (${reportData.normalRpmPercent}%)`, faClock, settings.showNormalRpmTime)}
                {renderReportRow('Время работы двигателя на предельных оборотах', `${reportData.maxRpmTime} (${reportData.maxRpmPercent}%)`, faClock, settings.showMaxRpmTime)}
                {renderReportRow('Время с выключенным двигателем', `${reportData.engineOffTime} (${reportData.engineOffTimePercent}%)`, faClock, settings.showEngineOffTime)}
                {renderReportRow('Время работы двигателя под нагрузкой', `${reportData.engineLoadTime} (${reportData.engineLoadTimePercent}%)`, faClock, settings.showEngineLoadTime)}
              </div>

              {/* Топливо */}
              <div className="report-section">
                <h3>Топливо</h3>
                {renderReportRow('Начальный объём, л', `${reportData.fuelStart} л`, faGasPump, settings.showFuelStart)}
                {renderReportRow('Конечный объём, л', `${reportData.fuelEnd} л`, faGasPump, settings.showFuelEnd)}
                {renderReportRow('Фактический расход, л', `${reportData.fuelConsumption} л`, faGasPump, settings.showFuelConsumption)}
                {renderReportRow('Объём заправок, л', `${reportData.fuelRefills} л`, faGasPump, settings.showFuelRefills)}
                {renderReportRow('Объём сливов, л', `${reportData.fuelDrains} л`, faGasPump, settings.showFuelDrains)}
                {renderReportRow('Минимальный объём, л', `${reportData.fuelMin} л`, faGasPump, settings.showFuelMin)}
                {renderReportRow('Максимальный объём, л', `${reportData.fuelMax} л`, faGasPump, settings.showFuelMax)}
                {renderReportRow('Фактический расход на 100 км, л', `${reportData.fuelPer100km} л`, faGasPump, settings.showFuelPer100km)}
                {renderReportRow('Фактический расход на 100 км в движении, л', `${reportData.fuelPer100kmMovement} л`, faGasPump, settings.showFuelPer100kmMovement)}
                {renderReportRow('Фактический расход в движении, л', `${reportData.fuelMovement} л`, faGasPump, settings.showFuelMovement)}
                {renderReportRow('Фактический расход без движения, л', `${reportData.fuelIdle} л`, faGasPump, settings.showFuelIdle)}
                {renderReportRow('Норма расхода на 100 км, л', `${reportData.fuelNorm} л`, faGasPump, settings.showFuelNorm)}
                {renderReportRow('Расчетный расход по норме на 100 км, л', `${reportData.fuelNormCalculated} л`, faGasPump, settings.showFuelNormCalculated)}
                {renderReportRow('Отклонение от нормы на 100 км, %', `${reportData.fuelDeviation}%`, faGasPump, settings.showFuelDeviation)}
                {renderReportRow('Перерасход от нормы на 100 км за период, л', `${reportData.fuelOverconsumption} л`, faGasPump, settings.showFuelOverconsumption)}
                {renderReportRow('Фактический расход за час работы двигателя, л', `${reportData.fuelPerHour} л`, faGasPump, settings.showFuelPerHour)}
                {renderReportRow('Фактический расход на моточас, л', `${reportData.fuelPerEngineHour} л`, faGasPump, settings.showFuelPerEngineHour)}
                {renderReportRow('Фактический расход за час работы двигателя без движения, л', `${reportData.fuelPerHourIdle} л`, faGasPump, settings.showFuelPerHourIdle)}
                {renderReportRow('Фактический расход за час работы двигателя в движении, л', `${reportData.fuelPerHourMovement} л`, faGasPump, settings.showFuelPerHourMovement)}
                {renderReportRow('Норма расхода на час работы двигателя, л', `${reportData.fuelNormPerHour} л`, faGasPump, settings.showFuelNormPerHour)}
                {renderReportRow('Расчетный расход по норме на час работы двигателя, л', `${reportData.fuelNormPerHourCalculated} л`, faGasPump, settings.showFuelNormPerHourCalculated)}
                {renderReportRow('Отклонение от нормы на час работы двигателя, %', `${reportData.fuelDeviationPerHour}%`, faGasPump, settings.showFuelDeviationPerHour)}
                {renderReportRow('Перерасход от нормы на час работы двигателя, л', `${reportData.fuelOverconsumptionPerHour} л`, faGasPump, settings.showFuelOverconsumptionPerHour)}
                {renderReportRow('Фактический расход за время работы двигателя, л', `${reportData.fuelTotal} л`, faGasPump, settings.showFuelTotal)}
                {renderReportRow('Фактический расход за время работы двигателя без движения, л', `${reportData.fuelTotalIdle} л`, faGasPump, settings.showFuelTotalIdle)}
                {renderReportRow('Превышение нормы расхода на холостом ходу', `${reportData.fuelIdleExcess} л`, faGasPump, settings.showFuelIdleExcess)}
              </div>

              {/* Счетчики */}
              <div className="report-section">
                <h3>Счетчики</h3>
                {renderReportRow('Значение счетчика моточасов, час:мин', reportData.engineHoursValue, faClock, settings.showEngineHoursValue)}
                {renderReportRow('Значение одометра CAN на начало периода, км', `${reportData.odometerStart} км`, faRoad, settings.showOdometerStart)}
                {renderReportRow('Значение одометра CAN на конец периода, км', `${reportData.odometerEnd} км`, faRoad, settings.showOdometerEnd)}
                {renderReportRow('Значение счетчика расхода топлива, л', `${reportData.fuelCounter} л`, faGasPump, settings.showFuelCounter)}
              </div>

              {/* Техобслуживание */}
              <div className="report-section">
                <h3>Техобслуживание</h3>
                {renderReportRow('Пробег до ТО, км', `${reportData.maintenanceMileage} км`, faRoad, settings.showMaintenanceMileage)}
                {renderReportRow('Моточасов до ТО, час', `${reportData.maintenanceHours} ч`, faClock, settings.showMaintenanceHours)}
              </div>

              {/* Итоги */}
              <div className="report-section summary">
                <h3>Итоги</h3>
                {renderReportRow('Пробег, км', `${reportData.summaryMileage} км`, faRoad, settings.showSummaryMileage)}
                {renderReportRow('Моточасы, час:мин', reportData.summaryHours, faClock, settings.showSummaryHours)}
                {renderReportRow('Расход топлива, л', `${reportData.summaryFuel} л`, faGasPump, settings.showSummaryFuel)}
              </div>
            </div>
          )
        ) : (
          <div className="no-data">
            <p>Нет данных для отображения</p>
            <p>Выберите транспортное средство и период</p>
          </div>
        )}
      </div>

      {/* Модальное окно настроек */}
      {showSettingsModal && (
        <div className="settings-modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="settings-modal" onClick={e => e.stopPropagation()}>
            <div className="settings-header">
              <h3>Настройки отчета</h3>
              <button className="close-btn" onClick={() => setShowSettingsModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="settings-content">
              <div className="settings-section">
                <h4>Основная информация</h4>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showVehicleGroup" 
                    checked={settings.showVehicleGroup}
                    onChange={() => handleSettingChange('showVehicleGroup')}
                  />
                  <label htmlFor="showVehicleGroup">Группа транспортных средств</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showVehicleName" 
                    checked={settings.showVehicleName}
                    onChange={() => handleSettingChange('showVehicleName')}
                  />
                  <label htmlFor="showVehicleName">Название ТС</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showDate" 
                    checked={settings.showDate}
                    onChange={() => handleSettingChange('showDate')}
                  />
                  <label htmlFor="showDate">Дата</label>
                </div>
              </div>

              <div className="settings-section">
                <h4>Пробег и счетчики</h4>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showMileage" 
                    checked={settings.showMileage}
                    onChange={() => handleSettingChange('showMileage')}
                  />
                  <label htmlFor="showMileage">Пробег</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showTotalMileage" 
                    checked={settings.showTotalMileage}
                    onChange={() => handleSettingChange('showTotalMileage')}
                  />
                  <label htmlFor="showTotalMileage">Общий пробег</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showEngineHours" 
                    checked={settings.showEngineHours}
                    onChange={() => handleSettingChange('showEngineHours')}
                  />
                  <label htmlFor="showEngineHours">Моточасы</label>
                </div>
              </div>

              <div className="settings-section">
                <h4>Скорость</h4>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showSpeedViolations" 
                    checked={settings.showSpeedViolations}
                    onChange={() => handleSettingChange('showSpeedViolations')}
                  />
                  <label htmlFor="showSpeedViolations">Превышения скорости</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showAverageSpeed" 
                    checked={settings.showAverageSpeed}
                    onChange={() => handleSettingChange('showAverageSpeed')}
                  />
                  <label htmlFor="showAverageSpeed">Средняя скорость</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showMaxSpeed" 
                    checked={settings.showMaxSpeed}
                    onChange={() => handleSettingChange('showMaxSpeed')}
                  />
                  <label htmlFor="showMaxSpeed">Максимальная скорость</label>
                </div>
              </div>

              <div className="settings-section">
                <h4>Время работы</h4>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showMovementTime" 
                    checked={settings.showMovementTime}
                    onChange={() => handleSettingChange('showMovementTime')}
                  />
                  <label htmlFor="showMovementTime">Время движения</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showEngineTime" 
                    checked={settings.showEngineTime}
                    onChange={() => handleSettingChange('showEngineTime')}
                  />
                  <label htmlFor="showEngineTime">Время работы двигателя</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showIdleTime" 
                    checked={settings.showIdleTime}
                    onChange={() => handleSettingChange('showIdleTime')}
                  />
                  <label htmlFor="showIdleTime">Время на холостом ходу</label>
                </div>
              </div>

              <div className="settings-section">
                <h4>Топливо</h4>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showFuelConsumption" 
                    checked={settings.showFuelConsumption}
                    onChange={() => handleSettingChange('showFuelConsumption')}
                  />
                  <label htmlFor="showFuelConsumption">Расход топлива</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showFuelRefills" 
                    checked={settings.showFuelRefills}
                    onChange={() => handleSettingChange('showFuelRefills')}
                  />
                  <label htmlFor="showFuelRefills">Заправки</label>
                </div>
                <div className="setting-item">
                  <input 
                    type="checkbox" 
                    id="showFuelDrains" 
                    checked={settings.showFuelDrains}
                    onChange={() => handleSettingChange('showFuelDrains')}
                  />
                  <label htmlFor="showFuelDrains">Сливы</label>
                </div>
              </div>
            </div>
            <div className="settings-footer">
              <button className="btn btn-secondary" onClick={() => setShowSettingsModal(false)}>
                Отмена
              </button>
              <button className="btn btn-primary" onClick={() => setShowSettingsModal(false)}>
                Применить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommonStatReport; 